import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Stack,
  Paper,
  Group,
  Badge,
  Button,
  SimpleGrid,
  Card,
  ThemeIcon,
  Divider,
  TextInput,
  ScrollArea,
  Tabs,
  ActionIcon,
  Collapse,
  Tooltip,
  Kbd,
} from '@mantine/core';
import { EmptyState } from '../DataDisplay';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconBuilding,
  IconFlag,
  IconGitCommit,
  IconActivity,
  IconPlugConnected,
  IconFolders,
  IconUsersGroup,
  IconTimeline,
  IconRefresh,
  IconDatabase,
  IconMap,
  IconArrowRight,
  IconSearch,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconExternalLink,
  IconSparkles,
  IconServer,
  IconCloudDownload,
  IconCloudUpload,
  IconRobot,
  IconActivityHeartbeat,
  IconHeartRateMonitor,
  IconGitBranch,
} from '@tabler/icons-react';
import './styles.css';
import { useEnvironment } from '../../hooks/useEnvironment';

interface ModuleInfo {
  id: string;
  title: string;
  path: string;
  icon: React.ComponentType<any>;
  description: string;
  features: string[];
  useCases: string[];
  color: string;
  category: 'management' | 'monitoring' | 'insights' | 'sync' | 'infrastructure' | 'onprem' | 'agent';
  tags: string[];
}

const modules: ModuleInfo[] = [
  {
    id: 'organizations',
    title: 'Organizations',
    path: '/organizations',
    icon: IconBuilding,
    description: 'Manage and view all organizations in the system. This module provides comprehensive organization management capabilities including status tracking, trial management, premium organization settings, and customer status management.',
    features: [
      'View all organizations with filtering by status (Active/Inactive/All)',
      'Premium column in table with visual indicators',
      'Update organization active status',
      'Manage trial start and end dates',
      'Set premium organization status (premium orgs don\'t show expired warnings)',
      'Update customer status via dropdown (PILOT, ACTIVE, PAID, FREE, CHURNED, PILOT_LOST)',
      'View organization statistics (total, active, premium, inactive)',
      'Search and filter organizations',
      'Detailed organization information drawer with all settings',
      'Column visibility and ordering controls',
      'Trial expiration warnings (excluded for premium organizations)',
    ],
    useCases: [
      'Monitor organization health and status',
      'Manage customer trials and subscriptions',
      'Identify premium vs standard organizations',
      'Track organization growth and metrics',
      'Update customer lifecycle status',
      'Manage trial periods and extensions',
      'Categorize organizations by customer status',
    ],
    color: 'blue',
    category: 'management',
    tags: ['organizations', 'management', 'customers', 'status', 'premium', 'trial', 'customer-status'],
  },
  {
    id: 'feature-management',
    title: 'Feature Management',
    path: '/feature-management',
    icon: IconFlag,
    description: 'Comprehensive feature flag management system with two-tab interface. Manage feature flag definitions globally and configure organization-specific feature flags with backend pagination, search, and confirmation modals with SQL query previews. All delete operations use soft delete (deactivation) to preserve data integrity. Includes intelligent cache management with refresh capabilities and optimized dark theme styling.',
    features: [
      'Feature Flag Definitions management (CRUD operations with soft delete)',
      'Soft delete/deactivation for definitions (preserves data, reversible)',
      'Organizations tab with feature flag status and enabled flags count',
      'Backend pagination and search for definitions (default 10 per page)',
      'Backend pagination and search for organizations (default 10 per page)',
      'Confirmation modals with SQL query previews for all operations',
      'Organization-specific feature flag management modal',
      'Add new flags to organizations from available definitions',
      'Real-time flag status tracking and updates',
      'Enhanced tab UI with icons and consistent styling',
      'Modal overlays with blur effects and smooth transitions',
      'Active/inactive state management for definitions',
      'Refresh button that fetches fresh data from database (bypasses cache)',
      'Comprehensive cache invalidation for all feature flag related queries',
      'Dark theme optimized badge styling for enabled flags display',
    ],
    useCases: [
      'Gradual feature rollouts with organization-level control',
      'A/B testing capabilities across different organizations',
      'Emergency feature disabling for specific organizations',
      'Organization-specific feature access management',
      'Global feature flag definition management',
      'Safe deactivation of feature flags (soft delete preserves data)',
      'Bulk feature flag operations with confirmation dialogs',
      'Search and filter feature flags across all organizations',
      'Data retention and audit trail for deactivated definitions',
    ],
    color: 'grape',
    category: 'management',
    tags: ['features', 'flags', 'toggles', 'configuration', 'definitions', 'organizations', 'pagination', 'search'],
  },
    {
      id: 'coding-metrics',
      title: 'Coding Metrics',
      path: '/coding-metrics',
      icon: IconGitCommit,
      description: 'Monitor and track processed/unprocessed commits and pull requests across organizations. View organization-specific metrics, trends, and repository breakdowns. Identify backlogs and abnormal repository behavior.',
      category: 'monitoring',
      features: [
        'Processing Queue tab - View repositories awaiting processing with date-wise breakdown and commit/PR counts',
        'Commits tab - Detailed commit metrics with date-wise breakdown and processing status',
        'Pull Requests tab - Pull request metrics, trends, and processing status',
        'Telemetry tab (conditional - only shown when telemetry is enabled) - Code telemetry analysis results with daily trends, status breakdowns (completed, pending, failed, no_changes), and AI/human line ratios',
        'Total, Processed, and Unprocessed Commits tracking',
        'Pull Request metrics and trends',
        'Abnormal repositories detection (repos with unprocessed commits spanning multiple days)',
        'Date-wise breakdown of coding activity',
        'Backend sorting and pagination for all tables',
        'Summary cards with status breakdowns (completed, pending, failed)',
        'Daily trend visualization charts',
        'Organization-specific filtering and date range selection (max 7 days)',
      ],
      useCases: [
        'Monitor pull request throughput',
        'Identify stuck PRs and repositories needing processing',
        'Track commit processing backlog',
        'Analyze code telemetry results and AI/human line ratios',
        'Monitor telemetry generation status for merged PRs',
        'View daily trends in merged PRs and telemetry status',
        'Identify repositories with processing delays',
      ],
      color: 'violet',
      tags: ['commits', 'pull requests', 'backlog', 'processing', 'alerts', 'repositories', 'organization', 'telemetry', 'ai-analysis', 'processing-queue'],
    },
  {
    id: 'webhooks',
    title: 'Webhook Insights',
    path: '/webhooks',
    icon: IconActivity,
    description: 'Analyze webhook delivery patterns, error rates, and performance metrics. Track webhook health across organizations and identify anomalies.',
    features: [
      'Daily webhook summaries and trends',
      'Error pattern analysis',
      'Top organizations by webhook volume',
      'Anomaly detection',
      'Orphaned webhook tracking',
      'Organization-specific webhook analytics',
      'Performance metrics and success rates',
    ],
    useCases: [
      'Monitor webhook delivery reliability',
      'Identify integration issues',
      'Track webhook volume trends',
      'Debug webhook failures',
    ],
    color: 'orange',
    category: 'monitoring',
    tags: ['webhooks', 'delivery', 'errors', 'analytics'],
  },
  {
    id: 'integration-stats',
    title: 'Integration Stats',
    path: '/integration-stats',
    icon: IconPlugConnected,
    description: 'Monitor integration client status and data freshness across organizations. Track which integrations are active, inactive, or missing data, and identify organizations with stale integration data.',
    features: [
      'Integration client status tracking (Active, Inactive, No Data)',
      'Data freshness monitoring (last activity timestamps)',
      'Organization-level integration health',
      'Integration type breakdown (GitHub, GitLab, Bitbucket, Azure, Jira, etc.)',
      'Clients without data identification',
      'Integration status filtering and search',
    ],
    useCases: [
      'Monitor integration health across organizations',
      'Identify integrations with stale or missing data',
      'Track integration adoption and usage',
      'Debug integration connectivity issues',
      'Ensure data freshness for all integrations',
    ],
    color: 'cyan',
    category: 'monitoring',
    tags: ['integrations', 'status', 'health', 'connectivity'],
  },
  {
    id: 'incremental-sync',
    title: 'Incremental Sync',
    path: '/incremental-sync',
    icon: IconTimeline,
    description: 'Monitor incremental synchronization processes across repositories. Track sync status, duration, errors, and API usage patterns to ensure data freshness.',
    features: [
      'Sync status dashboard',
      'Duration and performance metrics',
      'Error tracking and analysis',
      'API usage statistics',
      'Organization and repository-level sync details',
      'Daily repository state tracking',
      'Sync health indicators',
    ],
    useCases: [
      'Monitor data synchronization health',
      'Identify slow or failing syncs',
      'Track API usage and costs',
      'Debug synchronization issues',
      'Optimize sync performance',
    ],
    color: 'indigo',
    category: 'sync',
    tags: ['sync', 'synchronization', 'performance', 'api'],
  },
  {
    id: 'active-syncs',
    title: 'Active Syncs',
    path: '/active-syncs',
    icon: IconRefresh,
    description: 'View currently active synchronization processes in real-time. Monitor ongoing sync operations and their progress.',
    features: [
      'Real-time active sync monitoring',
      'Sync progress tracking',
      'Current sync status',
      'Active sync details',
    ],
    useCases: [
      'Monitor ongoing sync operations',
      'Track sync progress',
      'Identify long-running syncs',
      'Real-time system health monitoring',
    ],
    color: 'teal',
    category: 'sync',
    tags: ['sync', 'realtime', 'progress', 'monitoring'],
  },
  {
    id: 'jira-sync',
    title: 'Jira Sync',
    path: '/jira-sync',
    icon: IconPlugConnected,
    description: 'Comprehensive Jira synchronization monitoring dashboard. Track initial and incremental syncs, monitor board-level sync status, analyze API logs, and view hierarchical board structures with real-time visibility into sync processes.',
    features: [
      'Dashboard overview with active syncs, completion rates, and recent syncs',
      'Account Syncs tab - Organization-level sync status with board counts and progress',
      'Board Syncs tab - Board-level sync details grouped by day with lazy loading',
      'Hierarchy tab - Nested view of boards → sub-boards → sprints & issues with sync monitoring',
      'API Logs tab - API call statistics, success rates, error analysis, and unknown status tracking',
      'Date range filtering (max 7 days) with quick presets (3 days, 7 days)',
      'Real-time sync status tracking (In Progress, Completed, Failed, Pending)',
      'Error visibility with permission issue detection and detailed error messages',
      'Component-based architecture for maintainability',
      'Cache-aware refresh mechanism with loading indicators',
      'Pagination and search capabilities',
      'Sync progress monitoring (issues, sprints, sub-boards fetched)',
      'Unknown API call status tracking (NULL status values)',
    ],
    useCases: [
      'Monitor Jira integration sync health across organizations',
      'Track initial and incremental sync progress',
      'Identify sync failures and permission issues',
      'Analyze API call patterns and success rates',
      'Debug sync issues with detailed error messages',
      'View hierarchical board structures with sync status',
      'Monitor board-level sync completion and failures',
      'Track API log statistics and identify problematic API types',
      'Monitor syncs for specific date ranges',
      'Identify boards with permission errors or sync failures',
    ],
    color: 'cyan',
    category: 'sync',
    tags: ['jira', 'sync', 'monitoring', 'boards', 'api-logs', 'hierarchy', 'sprints', 'issues', 'integration'],
  },
  {
    id: 'repo-insights',
    title: 'Repository Insights',
    path: '/repo-insights',
    icon: IconFolders,
    description: 'Comprehensive repository analytics including volume, SCM provider distribution, inclusion/exclusion tracking, and repository trends over time.',
    features: [
      'Repository overview with total, included, and excluded counts',
      'SCM provider breakdown (GitHub, GitLab, Bitbucket, etc.)',
      'Inclusion and exclusion tracking with reasons',
      'Repository trends and growth charts',
      'New repository detection',
      'Status coverage analysis',
      'Excluded repository management',
    ],
    useCases: [
      'Monitor repository growth',
      'Track repository inclusion decisions',
      'Analyze SCM provider distribution',
      'Identify repositories needing attention',
      'Plan repository capacity',
    ],
    color: 'violet',
    category: 'insights',
    tags: ['repositories', 'analytics', 'trends', 'scm'],
  },
  {
    id: 'author-insights',
    title: 'Author Insights',
    path: '/author-insights',
    icon: IconUsersGroup,
    description: 'Track author population, activity, and merge patterns. Analyze author onboarding velocity, SCM provider distribution, identify merged author clusters, and correlate new author creation with integration events to distinguish normal vs abnormal growth.',
    features: [
      'Total, active, and inactive author counts',
      'Merged author analysis and clusters',
      'SCM provider comparison',
      'Daily author trends and charts with interactive filtering',
      'Author status breakdown',
      'Onboarding velocity metrics',
      'Merge group identification',
      'Integration correlation detection with provider names and timing',
      'Visual timeline showing which integrations led to author spikes',
      'Smart detection of normal vs abnormal author growth patterns',
    ],
    useCases: [
      'Monitor author population growth',
      'Track author activity patterns',
      'Identify duplicate author accounts',
      'Analyze author onboarding trends',
      'Understand SCM provider author distribution',
      'Distinguish normal author growth (from new integrations) vs abnormal spikes',
      'Validate that author increases are expected when organizations connect new tools',
      'Identify which specific integrations (GitHub, GitLab, etc.) caused author spikes',
      'Track integration-to-author correlation timing (same day, days before)',
    ],
    color: 'pink',
    category: 'insights',
    tags: ['authors', 'analytics', 'trends', 'onboarding', 'integration', 'correlation'],
  },
  {
    id: 'missing-data-insights',
    title: 'Missing Data Insights',
    path: '/missing-data-insights',
    icon: IconDatabase,
    description: 'Identify and track missing commits and pull requests across repositories. Monitor data completeness and identify gaps in data ingestion.',
    features: [
      'Missing commits tracking',
      'Missing pull requests tracking',
      'Repository-level missing data analysis',
      'Trends and patterns in missing data',
      'Data completeness metrics',
      'Gap identification and alerts',
    ],
    useCases: [
      'Monitor data ingestion completeness',
      'Identify data gaps',
      'Track missing data trends',
      'Debug data ingestion issues',
      'Ensure data quality',
    ],
    color: 'yellow',
    category: 'insights',
    tags: ['data', 'completeness', 'quality', 'gaps'],
  },
  {
    id: 'data-ingestion',
    title: 'Data Ingestion',
    path: '/data-ingestion',
    icon: IconCloudDownload,
    description: 'Monitor deployment and incident event ingestion across organizations. Track whether organizations are actively sending data and identify API key connection status (Generated vs Connected).',
    features: [
      'Deployment and incident event ingestion monitoring',
      'Organization-level data ingestion status tracking',
      'API key status monitoring (Generated vs Connected)',
      'Daily event counts visualization with status breakdowns',
      'Summary cards for selected range and last 30 days (pending/processed/failed)',
      'Failed/Pending deployment events table with pagination and errors',
      'Date range filtering (max 7 days)',
      'Organization-specific filtering',
      'Real-time data freshness indicators',
      'Cache status badges',
    ],
    useCases: [
      'Monitor if organizations are actively ingesting deployment events',
      'Track incident event ingestion across organizations',
      'Identify organizations with API keys that are Generated but not Connected',
      'Detect when organizations stop sending data',
      'Verify data ingestion health for specific organizations',
      'Track daily ingestion patterns and trends',
      'Ensure data pipeline is receiving events correctly',
    ],
    color: 'lime',
    category: 'monitoring',
    tags: ['data-ingestion', 'deployments', 'incidents', 'monitoring', 'api-keys', 'events'],
  },
  {
    id: 'ecs-deployments',
    title: 'ECS Deployments',
    path: '/ecs-deployments',
    icon: IconServer,
    description: 'Comprehensive ECS service monitoring with real-time health status, deployment tracking, crash analysis, and task definition management. Monitor service performance, identify issues, and track deployment progress across AWS ECS clusters. Includes automatic version tracking to compare current vs latest task definition revisions.',
    features: [
      'Real-time service health status (Healthy, Degraded, Crashed, Inactive)',
      'Service overview with total, healthy, degraded, crashed, and inactive counts',
      'Task utilization and cluster health metrics with progress indicators',
      'Automatic task definition version tracking (current vs latest revision)',
      'Version comparison with visual indicators for outdated services',
      'Click revision numbers to open task definitions in AWS Console',
      'Deployment in-progress detection and status tracking',
      'Services Needing Attention section with alerts and modal view',
      'Comprehensive deployment history with status and task counts',
      'Crash trend analysis with 7-day visualization charts',
      'Recent crashes monitoring with exit codes and reasons',
      'Service filtering and search capabilities',
      'Detailed service, deployment, and crash modals',
      'Cache status indicators for data freshness',
      'Task count tracking (running, desired, pending)',
      'Uptime and timing information per service',
    ],
    useCases: [
      'Monitor ECS service health and availability',
      'Track deployment frequency and success rates',
      'Identify services needing attention or updates',
      'Compare current vs latest task definition revisions (automatically loaded)',
      'Quickly identify outdated services that need updates',
      'Detect in-progress deployments and monitor progress',
      'Identify crash patterns and trends over time',
      'Debug service issues with detailed crash information',
      'Monitor cluster performance and task utilization',
      'Track service uptime and performance metrics',
      'Identify inactive or scaled-down services',
    ],
    color: 'green',
    category: 'infrastructure',
    tags: ['ecs', 'deployments', 'aws', 'containers', 'monitoring', 'task-definitions', 'crash-analysis', 'health-metrics'],
  },
  {
    id: 'migration',
    title: 'Migration',
    path: '/migration',
    icon: IconCloudUpload,
    description: 'Manage and execute organization data migrations to S3. Discover table data, analyze export impact, and perform secure migrations with support for dry runs and cleanup.',
    features: [
      'Organization data discovery and analysis',
      'Migratable vs Skipped table reporting',
      'Support for direct and indirect table relationships',
      'Dry Run mode for simulation',
      'Backup (Export Only) and Move (Export + Delete) modes',
      'Real-time migration progress tracking',
      'Detailed audit logs and history',
    ],
    useCases: [
      'Archive inactive organization data to S3',
      'Free up database space by offloading old data',
      'Create data backups for compliance',
      'Migrate organizations between environments',
    ],
    color: 'grape',
    category: 'infrastructure',
    tags: ['migration', 's3', 'export', 'backup', 'archive', 'data'],
  },
  {
    id: 'on-prem-organizations',
    title: 'On-Prem Organizations',
    path: '/on-prem/organizations',
    icon: IconBuilding,
    description: 'Manage on-premise client organizations and monitor fleet health. Features a Master-Detail interface for efficient client navigation and real-time service distribution matrix.',
    features: [
      'Master-Detail organization browser',
      'Fleet health and status monitoring (Online/Warning/Critical)',
      'Service distribution matrix per client',
      'Real-time health score tracking',
      'Client search and filtering',
    ],
    useCases: [
      'Identify organizations with degraded fleet health',
      'Analyze service version distribution for a specific client',
      'Track client "last seen" and connectivity metrics',
    ],
    color: 'blue',
    category: 'onprem',
    tags: ['on-prem', 'organizations', 'clients', 'fleet', 'health'],
  },
  {
    id: 'on-prem-releases',
    title: 'On-Prem Releases',
    path: '/on-prem/releases',
    icon: IconCloudUpload,
    description: 'Centralized registry for on-premise service builds and releases. Track build provenance, release notes, and governance lifecycle for all services in the catalogue.',
    features: [
      'Service-centric registry browser',
      'Build registration with branch and version tracking',
      'Governance status machine (Generated -> Testing -> Approved)',
      'Rich release and developer notes management',
      'ECR repository tracking with quick-copy utilities',
    ],
    useCases: [
      'Register new builds for production deployment',
      'Review release notes for specific versions',
      'Manage build lifecycle from generation to approval',
    ],
    color: 'cyan',
    category: 'onprem',
    tags: ['on-prem', 'releases', 'builds', 'registry', 'governance'],
  },
  {
    id: 'on-prem-history',
    title: 'On-Prem History',
    path: '/on-prem/history',
    icon: IconTimeline,
    description: 'Interactive "Time Machine" for on-premise deployments. Track the full audit trail of how service versions have propagated across client environments over time.',
    features: [
      'Interactive deployment timeline (Time Machine)',
      'Client-specific audit logs',
      'Version change tracking and diffs',
      'Historical connectivity and status records',
    ],
    useCases: [
      'Post-mortem deployment analysis',
      'Audit release propagation across the fleet',
      'Track historical update success rates',
    ],
    color: 'violet',
    category: 'onprem',
    tags: ['on-prem', 'history', 'timeline', 'audit', 'deployments'],
  },
  {
    id: 'agent-monitoring',
    title: 'Agent Monitoring',
    path: '/agent-monitoring',
    icon: IconActivityHeartbeat,
    description: 'Real-time health dashboard for all hivel-agent deployments. Monitor upload activity, batch job outcomes, sync mode (initial vs incremental), and stuck-upload alerts across every agent-enabled organization.',
    features: [
      'Per-org agent health cards with colored health indicators',
      'Upload activity (7-day counts, last upload timestamp)',
      'Batch job outcomes (completed, failed, last run)',
      'Sync mode classification: INITIAL vs INCREMENTAL',
      'Stuck-upload detection (PROCESSING uploads with no batch job)',
      'Deployment info per org (hivel_agent_deployment)',
      'Provider badge (JIRA / JIRA_SERVER)',
      'Organization name from canonical organization table',
    ],
    useCases: [
      'Quickly identify orgs with unhealthy agent sync pipelines',
      'Detect stuck or failed uploads before customers report issues',
      'Track which orgs are on initial vs incremental sync',
      'Monitor deployment health across all agent-enabled orgs',
    ],
    color: 'orange',
    category: 'agent',
    tags: ['agent', 'monitoring', 'health', 'sync', 'uploads', 'jira', 'hivel-agent'],
  },
  {
    id: 'agent-sync',
    title: 'Agent Sync Monitor',
    path: '/agent-sync',
    icon: IconRobot,
    description: 'Full pipeline visibility for the hivel-agent Jira sync: S3 uploads → Spring Batch processing → step-level entity counts → per-org sync state → daily trends. Five tabs cover every stage from upload queue to batch job execution.',
    features: [
      'Overview tab: summary KPIs + recent batch jobs with org, job ID, sync type, status, issues, duration',
      'Batch Jobs tab: job cards grid with step progress bar, entity counts, click-to-expand step modal',
      'Step modal: 10 Spring Batch steps with read/write counts, duration bars, status badges',
      'Upload Queue tab: grouped by job, collapsed by default, status + file-type badges, failed alerts',
      'Sync State tab: per-org last_synced_at watermark, sync mode, cursor, deployment info',
      'Trends tab: daily issue/job/entity charts with zero-fill, success rate KPI, deleted-issue alert',
      'Date range filtering up to 30 days with provider filter (Jira Cloud / Jira Server)',
      'Organization filter with all-org aggregation',
      'Cache-aware refresh mechanism',
      'Tab count badges for quick glance at data volume',
    ],
    useCases: [
      'Diagnose stuck uploads (PROCESSING with no batch job)',
      'Trace a specific job through all 10 Spring Batch steps',
      'Compare issue throughput across date ranges',
      'Identify orgs that have never completed an incremental sync',
      'Monitor daily entity write counts (users, sprints, projects, issues)',
      'Detect upload queue failures and retry storms',
    ],
    color: 'orange',
    category: 'agent',
    tags: ['agent', 'sync', 'batch', 'spring-batch', 'jira', 'upload-queue', 'trends', 'hivel-agent', 'pipeline'],
  },
  {
    id: 'jira-monitoring',
    title: 'Jira Monitoring',
    path: '/jira-monitoring',
    icon: IconHeartRateMonitor,
    description: 'Health dashboard for all Jira-connected organizations. Tracks legacy (direct sync) and extract (hivel-agent) pipelines side by side. Each org card shows health status, last issue timestamp, sync stats, and API error rates.',
    features: [
      'Tabbed view: Legacy (direct Jira sync) and Extract (hivel-agent) orgs',
      'Health badge per org: Healthy / Warning / Critical / No Data / Sync Disabled',
      'Summary strip: total, healthy, warning, critical, no_data, sync_disabled counts',
      'Last issue updated timestamp (max jira_updated_date) — shows when data last arrived',
      'Org filter chips: All active orgs vs Pilot-only (customer_status = PILOT)',
      'Per-card metrics: syncs 7d, failed boards, API error%, pending git mappings',
      'Sync-disabled orgs (daily_sync_enabled = false) marked separately — no false alarms',
      'Customer status badge (PILOT / PAID)',
      'Click card → org detail page with full sync history, board breakdown, daily volume',
      'Detail page: 3-day snapshot table, recent syncs, board breakdown, API error log',
      'Cache-aware refresh per filter combination',
    ],
    useCases: [
      'Spot orgs with stale or failed syncs before customers report issues',
      'Compare last issue vs last sync to detect delivery-accuracy-only orgs',
      'Filter to pilot orgs for onboarding health checks',
      'Drill into a specific org to diagnose board failures or API errors',
      'Track which orgs have pending git-Jira mappings blocking delivery accuracy',
    ],
    color: 'pink',
    category: 'health' as any,
    tags: ['jira', 'health', 'monitoring', 'sync', 'legacy', 'extract', 'hivel-agent', 'dashboard'],
  },
  {
    id: 'git-monitoring',
    title: 'Git Monitoring',
    path: '/git-monitoring',
    icon: IconGitBranch,
    description: 'Gate-based health dashboard for all SCM sync pipelines — GitHub, GitLab, Bitbucket, and Azure DevOps. One card per org+integration. Evaluates 7 health gates: sync freshness, data freshness, git-util enrichment rate, API failure rate, NOT_FOUND rate, hung calls, and integration state.',
    features: [
      'One card per SCM integration — orgs with multiple providers appear multiple times',
      'Health badge: Healthy / Warning / Critical / No Data / Stale Data / No Activity / Sync Disabled',
      'Summary strip: total, healthy, warning, critical, no_data, stale_data, sync_disabled counts',
      'Provider filter chips: All · GitHub · GitLab · Bitbucket · Azure DevOps',
      'Pilot-only filter (customer_status = PILOT)',
      'Per-card metrics: Git-util %, API err%, NOT_FOUND%, hung calls',
      'Per-card timestamps: Last commit, Last PR, Last sync',
      'Click card → detail page with task execution history, API error breakdown, commit activity chart',
      'Detail: 30-day commit activity bar chart with gap detection',
      'Detail: git-util enrichment section with per-repo unenriched breakdown',
      'Detail: API failure rate = (FAILED + FORBIDDEN) ÷ (total − hung) — matches gate logic',
      'Detail: top error messages classified by type (gateway, network, auth, silent drop)',
      'Cache-aware refresh (120s TTL)',
    ],
    useCases: [
      'Spot orgs with dead or failing SCM integrations before customers report missing data',
      'Identify integrations with high API failure rates or NOT_FOUND repos (deleted/renamed)',
      'Detect hung API calls invisible to normal failure-rate monitoring',
      'Check git-util enrichment rate — low % means linesAdded/rework/newwork blank in customer dashboard',
      'Find historical commit gaps indicating data loss periods',
      'Filter to pilot orgs for onboarding health checks',
      'Drill into a specific org+integration for full diagnostic data',
    ],
    color: 'teal',
    category: 'health' as any,
    tags: ['git', 'scm', 'github', 'gitlab', 'bitbucket', 'azure', 'health', 'monitoring', 'sync', 'git-util', 'api-errors'],
  },
];

const categoryLabels: Record<string, string> = {
  management: 'Management',
  monitoring: 'Monitoring',
  insights: 'Insights',
  sync: 'Synchronization',
  infrastructure: 'Infrastructure',
  onprem: 'On-Prem',
  agent: 'Hivel Agent',
  health: 'Health Dashboard',
};

const categoryColors: Record<string, string> = {
  management: 'blue',
  monitoring: 'orange',
  insights: 'violet',
  sync: 'indigo',
  infrastructure: 'grape',
  onprem: 'cyan',
  agent: 'orange',
  health: 'pink',
};

interface SitemapContentProps {
  onNavigate?: (path: string) => void;
  compact?: boolean;
}

export function SitemapContent({ onNavigate, compact = false }: SitemapContentProps) {
  const navigate = useNavigate();
  const { isOnPremAllowed } = useEnvironment();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery, 200);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [showShortcutHint, setShowShortcutHint] = useState(() => {
    if (typeof window === 'undefined') return true;
    const timesShown = Number(sessionStorage.getItem('sitemapShortcutShown') || '0');
    return timesShown < 3;
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search on mount and when drawer opens
  useEffect(() => {
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showShortcutHint || typeof window === 'undefined') return;
    const timer = setTimeout(() => {
      setShowShortcutHint(false);
      const currentCount = Number(sessionStorage.getItem('sitemapShortcutShown') || '0');
      sessionStorage.setItem('sitemapShortcutShown', String(currentCount + 1));
    }, 3500);
    return () => clearTimeout(timer);
  }, [showShortcutHint]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape to clear search
      if (e.key === 'Escape' && searchQuery) {
        setSearchQuery('');
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  const handleModuleClick = (path: string) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const handleExpand = (moduleId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  // Filter modules based on search and category
  const filteredModules = useMemo(() => {
    let filtered = modules;

    // Filter by environment access
    if (!isOnPremAllowed) {
      filtered = filtered.filter((m) => m.category !== 'onprem');
    }

    // Filter by category
    if (activeCategory) {
      filtered = filtered.filter((m) => m.category === activeCategory);
    }

    // Filter by search query
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      filtered = filtered.filter(
        (m) =>
          m.title.toLowerCase().includes(query) ||
          m.description.toLowerCase().includes(query) ||
          m.path.toLowerCase().includes(query) ||
          m.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          m.features.some((f) => f.toLowerCase().includes(query)) ||
          m.useCases.some((u) => u.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [debouncedSearch, activeCategory]);

  // Get all available categories (always show all, not just filtered ones)
  const allCategories = useMemo(() => {
    const uniqueCategories = new Set<ModuleInfo['category']>();
    modules.forEach((module) => uniqueCategories.add(module.category));
    return Array.from(uniqueCategories) as ModuleInfo['category'][];
  }, []);

  // Group modules by category
  const groupedModules = useMemo(() => {
    const groups: Record<string, ModuleInfo[]> = {};
    filteredModules.forEach((module) => {
      if (!groups[module.category]) {
        groups[module.category] = [];
      }
      groups[module.category].push(module);
    });
    return groups;
  }, [filteredModules]);

  // Use all categories for tabs, but only show categories that have filtered results in the list
  const categories = Object.keys(groupedModules) as ModuleInfo['category'][];

  return (
    <Box className="sitemap-content fade-in" aria-label="Sitemap navigation">
      {/* Header */}
      <Stack gap="md" mb="xl">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon size={48} radius="xl" color="violet" variant="light" aria-hidden="true">
            <IconMap size={28} />
          </ThemeIcon>
          <div style={{ flex: 1 }}>
            <Title order={2} id="sitemap-title">
              Explore modules
            </Title>
            <Text size="sm" c="dimmed" mt={4}>
              Explore all {modules.length} modules. Search, filter by category, or click to learn more.
            </Text>
          </div>
        </Group>

        {/* Search Bar */}
        <TextInput
          ref={searchInputRef}
          placeholder="Search modules by name, description, or features..."
          leftSection={<IconSearch size={18} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                size="sm"
                variant="transparent"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Clear search"
              >
                <IconX size={16} />
              </ActionIcon>
            ) : showShortcutHint ? (
              <Group gap={4} style={{ pointerEvents: 'none' }}>
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </Group>
            ) : null
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          size="md"
          aria-label="Search modules"
          aria-describedby="sitemap-title"
          style={{ flex: 1 }}
        />
      </Stack>

      {/* Category Tabs */}
      {!debouncedSearch && (
        <Tabs
          value={activeCategory || 'all'}
          onChange={(value) => setActiveCategory(value === 'all' ? null : value)}
          mb="lg"
          aria-label="Filter by category"
        >
          <Tabs.List>
            <Tabs.Tab
              value="all"
              leftSection={<IconMap size={16} />}
            >
              All ({modules.length})
            </Tabs.Tab>
            {allCategories.map((category) => {
              const totalInCategory = modules.filter((m) => m.category === category).length;
              return (
                <Tabs.Tab
                  key={category}
                  value={category}
                  leftSection={
                    <Badge size="xs" variant="light" color={categoryColors[category]}>
                      {totalInCategory}
                    </Badge>
                  }
                >
                  {categoryLabels[category]}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>
      )}

      {/* Results Count */}
      {debouncedSearch && (
        <Text size="sm" c="dimmed" mb="md">
          Found {filteredModules.length} module{filteredModules.length !== 1 ? 's' : ''} matching "{debouncedSearch}"
        </Text>
      )}

      {/* Modules List */}
      <ScrollArea h={compact ? 'calc(100vh - 300px)' : undefined}>
        <Stack gap="md">
          {categories.length === 0 ? (
            <EmptyState
              icon={IconSearch}
              title="No modules found"
              description="Try adjusting your search query or category filter"
            >
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory(null);
                  }}
                >
                  Clear filters
                </Button>
            </EmptyState>
          ) : (
            categories.map((category) => (
              <Box key={category}>
                {categories.length > 1 && (
                  <Group gap="xs" mb="sm" mt={category !== categories[0] ? 'md' : undefined}>
                    <Badge
                      size="lg"
                      variant="light"
                      color={categoryColors[category]}
                      leftSection={<IconSparkles size={14} />}
                    >
                      {categoryLabels[category]}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {groupedModules[category].length} module{groupedModules[category].length !== 1 ? 's' : ''}
                    </Text>
                  </Group>
                )}

                <SimpleGrid cols={{ base: 1, sm: compact ? 1 : 2 }} spacing="md">
                  {groupedModules[category].map((module) => {
                    const Icon = module.icon;
                    const isExpanded = expandedModule === module.id;

                    return (
                      <Card
                        key={module.id}
                        shadow="sm"
                        padding="md"
                        radius="md"
                        withBorder
                        className="sitemap-module-card"
                        style={{
                          borderColor: isExpanded
                            ? `var(--mantine-color-${module.color}-6)`
                            : undefined,
                          borderWidth: isExpanded ? 2 : 1,
                          cursor: 'pointer',
                        }}
                        role="article"
                        aria-labelledby={`module-${module.id}-title`}
                        aria-expanded={isExpanded}
                        onClick={() => handleExpand(module.id)}
                      >
                        <Stack gap="sm">
                          {/* Module Header */}
                          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                              <ThemeIcon
                                size={36}
                                radius="md"
                                color={module.color}
                                variant="light"
                                aria-hidden="true"
                              >
                                <Icon size={18} />
                              </ThemeIcon>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Text
                                  id={`module-${module.id}-title`}
                                  fw={600}
                                  size="md"
                                  lineClamp={1}
                                  style={{ wordBreak: 'break-word' }}
                                >
                                  {module.title}
                                </Text>
                                <Group gap={4} mt={2}>
                                  <Text size="xs" c="dimmed" lineClamp={1} style={{ fontFamily: 'monospace' }}>
                                    {module.path}
                                  </Text>
                                  <Badge size="xs" variant="dot" color={module.color}>
                                    {module.category}
                                  </Badge>
                                </Group>
                              </div>
                            </Group>
                            <Tooltip label={isExpanded ? 'Collapse' : 'Expand details'}>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpand(module.id, e);
                                }}
                                aria-label={isExpanded ? 'Collapse module details' : 'Expand module details'}
                                aria-expanded={isExpanded}
                              >
                                {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                              </ActionIcon>
                            </Tooltip>
                          </Group>

                          {/* Description */}
                          <Text size="sm" c="dimmed" lineClamp={isExpanded ? undefined : 2}>
                            {module.description}
                          </Text>

                          {/* Tags */}
                          {module.tags.length > 0 && (
                            <Group gap={4} wrap="wrap">
                              {module.tags.slice(0, 3).map((tag) => (
                                <Badge key={tag} size="xs" variant="outline" color="gray">
                                  {tag}
                                </Badge>
                              ))}
                              {module.tags.length > 3 && (
                                <Badge size="xs" variant="outline" color="gray">
                                  +{module.tags.length - 3}
                                </Badge>
                              )}
                            </Group>
                          )}

                          {/* Expanded Details */}
                          <Collapse in={isExpanded}>
                            <Stack gap="md" mt="sm">
                              <Divider />

                              {/* Features */}
                              <div>
                                <Text fw={600} size="sm" mb="xs">
                                  Key Features
                                </Text>
                                <Stack gap={4}>
                                  {module.features.map((feature, idx) => (
                                    <Group key={idx} gap="xs" align="flex-start" wrap="nowrap">
                                      <IconSparkles
                                        size={14}
                                        style={{
                                          color: `var(--mantine-color-${module.color}-6)`,
                                          marginTop: 2,
                                          flexShrink: 0,
                                        }}
                                        aria-hidden="true"
                                      />
                                      <Text size="xs" style={{ flex: 1 }}>
                                        {feature}
                                      </Text>
                                    </Group>
                                  ))}
                                </Stack>
                              </div>

                              {/* Use Cases */}
                              <div>
                                <Text fw={600} size="sm" mb="xs">
                                  Common Use Cases
                                </Text>
                                <Stack gap={4}>
                                  {module.useCases.map((useCase, idx) => (
                                    <Group key={idx} gap="xs" align="flex-start" wrap="nowrap">
                                      <IconSparkles
                                        size={14}
                                        style={{
                                          color: `var(--mantine-color-${module.color}-6)`,
                                          marginTop: 2,
                                          flexShrink: 0,
                                        }}
                                        aria-hidden="true"
                                      />
                                      <Text size="xs" style={{ flex: 1 }}>
                                        {useCase}
                                      </Text>
                                    </Group>
                                  ))}
                                </Stack>
                              </div>

                              <Divider />

                              {/* Action Buttons */}
                              <Group gap="xs">
                                <Button
                                  variant="light"
                                  color={module.color}
                                  rightSection={<IconArrowRight size={16} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleModuleClick(module.path);
                                  }}
                                  style={{ flex: 1 }}
                                  aria-label={`Navigate to ${module.title}`}
                                >
                                  Open Module
                                </Button>
                                <Tooltip label="Open in new tab">
                                  <ActionIcon
                                    variant="light"
                                    color={module.color}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(module.path, '_blank');
                                    }}
                                    aria-label={`Open ${module.title} in new tab`}
                                  >
                                    <IconExternalLink size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Stack>
                          </Collapse>

                          {/* Collapsed Footer */}
                          {!isExpanded && (
                            <Group justify="space-between" mt="xs">
                              <Badge variant="light" color={module.color} size="sm">
                                {module.features.length} features
                              </Badge>
                              <Button
                                size="xs"
                                variant="subtle"
                                color={module.color}
                                rightSection={<IconArrowRight size={14} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleModuleClick(module.path);
                                }}
                                aria-label={`Quick navigate to ${module.title}`}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                Open
                              </Button>
                            </Group>
                          )}
                        </Stack>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </Box>
            ))
          )}
        </Stack>
      </ScrollArea>

      {/* Footer Summary */}
      {!debouncedSearch && !activeCategory && (
        <Paper shadow="sm" radius="md" withBorder p="md" mt="lg" style={{ backgroundColor: 'var(--mantine-color-violet-0)' }}>
          <Group justify="space-between" align="center" wrap="wrap">
            <div>
              <Text fw={600} size="sm" mb={4}>
                Platform Overview
              </Text>
              <Text size="xs" c="dimmed">
                {modules.length} comprehensive modules for managing organizations, monitoring data ingestion,
                tracking insights, and ensuring system health. All pages use a unified design system with
                reusable components including StatsCard, PageHeader, PageSection, FilterBar, DataTable,
                EmptyState, OrganizationSelector, DateRangePicker, RefreshButton, ConfirmationModal,
                PinEntryModal, and more for consistent UI/UX across the platform.
              </Text>
            </div>
            <Badge size="lg" variant="light" color="violet" leftSection={<IconSparkles size={16} />}>
              {modules.length} Modules
            </Badge>
          </Group>
        </Paper>
      )}
    </Box>
  );
}
